// src/widgets/TermsWidget.jsx
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { X, FileText } from 'lucide-react'

const termsContent = (
  <div className="space-y-6 text-sm leading-6 text-gray-800">
    <div className="space-y-1">
      <h3 className="text-xl font-semibold text-gray-900">ข้อตกลงและเงื่อนไขการใช้บริการ (Terms & Conditions)</h3>
      <div className="text-xs text-gray-500">มีผลบังคับใช้: 17 กันยายน 2025</div>
      <p>การเข้าถึงหรือใช้งาน Kuizy ถือว่าคุณยอมรับข้อตกลงฉบับนี้ โปรดอ่านอย่างละเอียด เอกสารนี้เป็นข้อตกลงระหว่างคุณกับ Kuizy เกี่ยวกับการใช้งานเว็บไซต์ แอป และบริการที่เกี่ยวข้องทั้งหมด</p>
    </div>

    <section className="space-y-2">
      <h4 className="font-semibold">1) คำจำกัดความ</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>“บริการ” หมายถึง แพลตฟอร์ม Kuizy รวมหน้าเว็บ แอป ชุดข้อสอบ คลังข้อสอบ ระบบคลาสเรียน ระบบรายงาน และ API ที่เกี่ยวข้อง</li>
        <li>“ผู้ใช้” หมายถึง บุคคลที่สมัครหรือใช้งานบริการ</li>
        <li>“องค์กร” หมายถึง หน่วยงาน/สถาบันที่จัดการหลักสูตร บทเรียน และคลาสเรียนบน Kuizy</li>
        <li>“เนื้อหาผู้ใช้” รวมถึงข้อความ ไฟล์สื่อ คำถาม/เฉลยข้อสอบ คำตอบ ผลคะแนน ความเห็น และข้อมูลอื่นที่ผู้ใช้สร้างหรืออัปโหลด</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">2) การยอมรับและการเปลี่ยนแปลงข้อตกลง</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>เราอาจปรับปรุงข้อตกลงเมื่อมีเหตุสมควร โดยประกาศในระบบ การใช้งานต่อถือว่ายอมรับเวอร์ชันที่ปรับปรุง</li>
        <li>หากไม่เห็นด้วย คุณควรหยุดใช้งานและยุติบัญชีตามข้อ 16</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">3) การลงทะเบียนและความเหมาะสม</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ต้องให้ข้อมูลที่ถูกต้อง เป็นปัจจุบัน และรักษาความปลอดภัยของบัญชี</li>
        <li>หากเป็นผู้เยาว์ ต้องปฏิบัติตามข้อกำหนดความยินยอมของผู้ปกครองตามกฎหมายไทย</li>
        <li>เราสงวนสิทธิ์ระงับหรือยุติบัญชีที่ฝ่าฝืนข้อตกลงหรือนโยบาย</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">4) สิทธิ์การใช้งาน</h4>
      <p>มอบสิทธิ์แบบไม่ผูกขาด ไม่อาจโอน และเพิกถอนได้เพื่อใช้บริการเพื่อวัตถุประสงค์ด้านการเรียน การสอน และการสอบตามที่แพลตฟอร์มออกแบบไว้เท่านั้น</p>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">5) ข้อห้ามใช้งาน</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ทุจริตการสอบ แอบอ้างบุคคลอื่น เลี่ยงมาตรการความซื่อสัตย์ หรือรบกวนระบบ</li>
        <li>ละเมิดกฎหมาย ทรัพย์สินทางปัญญา ความเป็นส่วนบุคคล หรือสิทธิของบุคคลที่สาม</li>
        <li>ทำวิศวกรรมย้อนกลับ สแกนช่องโหว่ ใช้สคริปต์/บอทโดยไม่ได้รับอนุญาต</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">6) เนื้อหาผู้ใช้และสิทธิ์</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>คุณยังคงเป็นเจ้าของเนื้อหาผู้ใช้ของคุณ</li>
        <li>คุณอนุญาตให้ Kuizy ใช้ จัดเก็บ ทำซ้ำ แปล ดัดแปลง และแสดงผลเท่าที่จำเป็นเพื่อให้บริการ ปรับปรุงคุณภาพ สำรองข้อมูล และสนับสนุนลูกค้า</li>
        <li>คุณรับรองว่าคุณมีสิทธิ์ตามกฎหมายในเนื้อหาที่อัปโหลด รวมถึงข้อสอบและสื่อประกอบ</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">7) ความซื่อสัตย์ของการสอบและการกำกับดูแล</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ผู้ดูแลอาจกำหนดเวลา จำนวนครั้ง เกณฑ์ผ่าน การสุ่มข้อสอบ และการแสดงเฉลย</li>
        <li>หากเปิดใช้การกำกับดูแลการสอบ (proctoring) อาจมีการเข้าถึงกล้อง ไมโครโฟน บันทึกเหตุการณ์ หรือภาพหน้าจอ โดยจะแจ้งให้ทราบล่วงหน้าและเก็บเท่าที่จำเป็น</li>
        <li>เราสงวนสิทธิ์ยกเลิกคะแนน ระงับสิทธิ์สอบ หรือปิดบัญชีเมื่อพบการทุจริต</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">8) บริการแบบมีค่าใช้จ่าย</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ราคาค่าบริการ ภาษี รอบบิล การต่ออายุอัตโนมัติ และเงื่อนไขการคืนเงิน (ถ้ามี) ระบุในหน้าสรุปแผน</li>
        <li>คุณยอมรับให้เรียกเก็บเงินผ่านผู้ให้บริการชำระเงินภายนอกตามข้อตกลงของผู้ให้บริการนั้น</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">9) บริการและลิงก์ของบุคคลที่สาม</h4>
      <p>การใช้งานบริการภายนอกอยู่ภายใต้เงื่อนไขของผู้ให้บริการนั้น Kuizy ไม่รับผิดชอบต่อความเสียหายอันเกิดจากบริการภายนอก</p>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">10) ความพร้อมใช้งาน การบำรุงรักษา และการเปลี่ยนแปลง</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>อาจมีการหยุดให้บริการชั่วคราวเพื่อบำรุงรักษาหรือเหตุสุดวิสัย โดยพยายามลดผลกระทบ</li>
        <li>เราอาจปรับเปลี่ยนฟีเจอร์หรืออินเทอร์เฟซ โดยยังคงวัตถุประสงค์หลักของบริการ</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">11) ทรัพย์สินทางปัญญา</h4>
      <p>ซอฟต์แวร์ โลโก้ ชื่อทางการค้า และองค์ประกอบของ Kuizy เป็นทรัพย์สินของเรา ห้ามใช้โดยไม่ได้รับอนุญาต</p>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">12) ข้อจำกัดความรับผิด</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>บริการให้ “ตามสภาพ” โดยไม่มีการรับประกัน</li>
        <li>เท่าที่กฎหมายอนุญาต ความรับผิดสูงสุดรวมของเราต่อคุณไม่เกินค่าธรรมเนียมที่คุณชำระใน 12 เดือนก่อนเหตุอันก่อให้เกิดความรับผิด</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">13) การชดใช้ค่าเสียหาย</h4>
      <p>คุณยินยอมชดใช้และคุ้มครอง Kuizy จากการเรียกร้องอันเกิดจากการใช้บริการที่ฝ่าฝืนข้อตกลง หรือละเมิดสิทธิของบุคคลที่สามโดยคุณ</p>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">14) เหตุสุดวิสัย</h4>
      <p>เราไม่ต้องรับผิดหากไม่สามารถให้บริการได้เนื่องมาจากเหตุสุดวิสัย เช่น ภัยพิบัติ ความขัดข้องของโครงข่ายอินเทอร์เน็ต ไฟฟ้าขัดข้อง หรือเหตุทางกฎหมาย</p>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">15) กฎหมายที่ใช้บังคับและเขตอำนาจศาล</h4>
      <p>ข้อตกลงนี้อยู่ภายใต้กฎหมายไทย และอยู่ในเขตอำนาจศาลไทย (กรุงเทพมหานคร)</p>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">16) การยุติและผลของการยุติ</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>คุณอาจยุติการใช้บริการได้ทุกเมื่อ บางเงื่อนไขยังคงมีผล ได้แก่ ทรัพย์สินทางปัญญา ข้อจำกัดความรับผิด การชดใช้ และข้อพิพาท</li>
        <li>คำขอสำเนาข้อมูลหรือการลบข้อมูลให้ดำเนินการตามนโยบายความเป็นส่วนบุคคล (PDPA)</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">17) ข้อทั่วไป</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>การโอนสิทธิและหน้าที่ต้องได้รับความยินยอมเป็นลายลักษณ์อักษร</li>
        <li>หากข้อกำหนดใดไม่ชอบด้วยกฎหมาย ส่วนที่เหลือยังคงมีผลบังคับ</li>
        <li>ฉบับภาษาไทยเป็นฉบับที่มีผลหากข้อความต่างจากภาษาอื่น</li>
        <li>ช่องทางติดต่อ: support@kuizy.ai</li>
      </ul>
    </section>
  </div>
)

export function TermsModal({ open, onClose, content }) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[999]">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }} className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b">
                <div className="flex items-center gap-2 text-gray-900 font-semibold"><FileText className="h-5 w-5" /> ข้อกำหนดและเงื่อนไขการใช้บริการ</div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{content || termsContent}</div>
              <div className="px-6 py-4 border-t flex justify-end">
                <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-900 text-white">ปิด</button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export function TermsCheckbox({ checked, onChange, content }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <label className="flex items-start gap-3 text-sm text-gray-700">
        <input type="checkbox" className="mt-1 h-4 w-4 rounded border-gray-300" checked={checked} onChange={e => onChange?.(e.target.checked)} />
        <span>ฉันได้อ่านและยอมรับ <button type="button" className="underline text-primary" onClick={() => setOpen(true)}>ข้อกำหนดและเงื่อนไข</button> ของ Kuizy</span>
      </label>
      <TermsModal open={open} onClose={() => setOpen(false)} content={content} />
    </>
  )
}

export default TermsCheckbox
